/* eslint-disable @typescript-eslint/no-misused-promises */
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  milestoneSchema,
  MilestoneSchema,
  recurringSchema,
  RecurringSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeftRight, Trash } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CurrencyInput, Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AccordionContent,
  Accordion,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { useCreateBillingPlan } from "./mutation";
import { DatePicker } from "@/components/ui/date-picker";
import { AdminJobCandidateDetails } from "@/queries/admin/job-details";

import { NewBillingPlan, Offer, OfferTerm } from "@/types";

enum MODE {
  RECURRING = "recurring",
  MILESTONE = "milestone",
  FULLTIME = "full-time",
}

const CreateBillingPlanForm = ({
  jobOpeningId,
  candidate,
  onCancel,
  onSuccess,
}: {
  jobOpeningId: string;
  candidate: AdminJobCandidateDetails;
  onCancel: () => void;
  onSuccess?: (data: AdminJobCandidateDetails) => void;
}) => {
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [mode, setMode] = useState<MODE>(MODE.RECURRING);

  useEffect(() => {
    const offer = candidate.offers.at(-1);
    const termType = offer?.terms[0]?.termType;
    if (!termType) {
      return;
    }

    if (termType === "milestone") {
      setMode(MODE.MILESTONE);
    } else if (termType === "recurring") {
      setMode(MODE.RECURRING);
    } else if (termType === "full-time") {
      setMode(MODE.FULLTIME);
    }
  }, [candidate]);

  const { createBillingPlan, isCreatingBillingPlan, createBillingPlanError } =
    useCreateBillingPlan({
      jobOpeningId,
      candidateStatusId: candidate.candidateStatusId,
      onSuccess,
    });

  const offer = candidate.offers.at(-1);

  return (
    <>
      {offer && (
        <Accordion
          type="single"
          value={accordionValue}
          onValueChange={setAccordionValue}
          collapsible
          className="mb-4"
        >
          <AccordionItem value="toggler">
            <AccordionTrigger>View Offer Details</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <h3 className="text-base text-[#2E2E38] font-semibold">
                    Offer status:
                  </h3>
                  <p className="text-base text-brand-neutral-black-7 capitalize">
                    {offer.status}
                  </p>
                </div>
                {offer.terms.map((term, idx) => (
                  <OfferTermSection key={`term-${idx}`} term={term} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {mode === MODE.RECURRING ? (
        <RecurringOfferForm
          offer={offer}
          createBillingPlanError={createBillingPlanError}
          isCreatingBillingPlan={isCreatingBillingPlan}
          switchMode={() => setMode(MODE.MILESTONE)}
          createBillingPlan={createBillingPlan}
          prefillComplete={() => setAccordionValue("")}
          onCancel={onCancel}
        />
      ) : mode === MODE.MILESTONE ? (
        <MilestoneOfferForm
          offer={offer}
          createBillingPlanError={createBillingPlanError}
          isCreatingBillingPlan={isCreatingBillingPlan}
          switchMode={() => setMode(MODE.RECURRING)}
          createBillingPlan={createBillingPlan}
          prefillComplete={() => setAccordionValue("")}
          onCancel={onCancel}
        />
      ) : mode === MODE.FULLTIME ? (
        <p>Full-time billing is not yet supported.</p>
      ) : undefined}
    </>
  );
};

const OfferTermSection = ({ term }: { term: OfferTerm }) => {
  if (term.termType === "milestone") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">Milestone</h3>
          <p className="text-base text-brand-neutral-black-7">
            {term.milestoneLabel ?? "Unnamed"}
          </p>
        </div>
        <div className="flex gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
      </div>
    );
  } else if (term.termType === "recurring") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly retainer:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Monthly fee:{" "}
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Billing details:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Duration: {term.iterations ? `${term.iterations} months` : "TBD"}
          </p>
          <p className="text-base text-brand-neutral-black-7">
            Billed on: {term.billingDay} of every month
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Candidate fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            Salary:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(term.totalAmountCents / 100)}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Venturous fee:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {typeof term.platformFeePercentage === "number"
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  (term.totalAmountCents * (term.platformFeePercentage / 100)) /
                    100,
                )
              : "TBD"}{" "}
            (plus tax)
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-base text-[#2E2E38] font-semibold">
            Start date:
          </h3>
          <p className="text-base text-brand-neutral-black-7">
            {new Date(term.startDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }
};

const MilestoneOfferForm = ({
  offer,
  createBillingPlanError,
  isCreatingBillingPlan,
  switchMode,
  createBillingPlan,
  prefillComplete,
  onCancel,
}: {
  offer: Offer | undefined;
  createBillingPlanError: Error | null;
  isCreatingBillingPlan: boolean;
  switchMode: () => void;
  createBillingPlan: (data: NewBillingPlan) => void;
  prefillComplete: () => void;
  onCancel: () => void;
}) => {
  const form = useForm<MilestoneSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(milestoneSchema)(data, context, opts);
    },
    defaultValues: {
      terms: [
        {
          milestoneLabel: "",
          candidateAmountCents: 0,
          platformFeeCents: 0,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "terms",
  });

  function onSubmit(data: MilestoneSchema) {
    if (isCreatingBillingPlan) return;

    createBillingPlan({
      firstBillingDate: data.firstBillingDate.toISOString(),
      terms: data.terms.map((term) => ({
        termType: "milestone",
        milestoneLabel: term.milestoneLabel,
        candidateAmountCents: term.candidateAmountCents,
        platformFeeCents: term.platformFeeCents,
        totalAmountCents: term.platformFeeCents + term.candidateAmountCents,
      })),
    });
  }

  function prefill() {
    prefillComplete();
    if (!offer) return;

    const firstTerm = offer.terms[0];
    if (!firstTerm) return;
    if (firstTerm.termType !== "milestone") return;

    form.setValue(
      "terms",
      offer.terms.map((term) => ({
        // Convert cents to dollars for the form
        milestoneLabel:
          term.termType === "milestone" ? term.milestoneLabel : "",
        candidateAmountCents: term.totalAmountCents,
        platformFeeCents:
          typeof term.platformFeePercentage === "number"
            ? Math.round(
                // Divide by 100 to convert percentage to decimal
                (term.totalAmountCents * term.platformFeePercentage) / 100,
              )
            : 0,
      })),
    );
  }

  return (
    <>
      <div className="my-3 flex justify-between">
        <Button variant="stroke" className="my-2" onClick={switchMode}>
          <ArrowLeftRight />
          Switch to recurring billing
        </Button>
        {offer?.terms[0]?.termType === "milestone" && (
          <Button variant="lightSecondary" className="mt-2" onClick={prefill}>
            Prefill From Offer
          </Button>
        )}
      </div>
      <Form {...form}>
        {createBillingPlanError && (
          <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
            Something went wrong when making a billing plan, please try again
            later.
          </p>
        )}
        <form
          className="space-y-4 w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="space-y-4">
            {fields.map((item, index) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-4 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Milestone {index + 1}
                  </h3>

                  {index > 0 && (
                    <Button
                      variant="ghostWithIcon"
                      onClick={() => remove(index)}
                    >
                      <Trash />
                    </Button>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`terms.${index}.milestoneLabel`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Milestone label</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Milestone name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`terms.${index}.candidateAmountCents`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Candidate fee</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          valueCents={field.value}
                          onValueCentsChange={field.onChange}
                          minCents={0}
                          placeholder="10,000"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`terms.${index}.platformFeeCents`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Venturous fee</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          valueCents={field.value}
                          onValueCentsChange={field.onChange}
                          minCents={0}
                          placeholder="10,000"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}
            <Button
              variant="ghost"
              type="button"
              onClick={() =>
                append({
                  milestoneLabel: "",
                  candidateAmountCents: 0,
                  platformFeeCents: 0,
                })
              }
            >
              + Add milestone
            </Button>
          </div>

          <FormField
            control={form.control}
            name="firstBillingDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>First billing date</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    modal
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="h-[1px] bg-[#E4E7EC] w-full" />
          <div className="!mb-1 flex justify-between">
            <Button variant="stroke" className="mt-2" onClick={onCancel}>
              Cancel
            </Button>

            <Button
              variant="success"
              type="submit"
              className="mt-2"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={form.handleSubmit(onSubmit)}
            >
              Create Billing Plan
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};

const RecurringOfferForm = ({
  offer,
  createBillingPlanError,
  isCreatingBillingPlan,
  switchMode,
  createBillingPlan,
  prefillComplete,
  onCancel,
}: {
  offer: Offer | undefined;
  createBillingPlanError: Error | null;
  isCreatingBillingPlan: boolean;
  switchMode: () => void;
  createBillingPlan: (data: NewBillingPlan) => void;
  prefillComplete: () => void;
  onCancel: () => void;
}) => {
  const form = useForm<RecurringSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(recurringSchema)(data, context, opts);
    },
  });

  function onSubmit(data: RecurringSchema) {
    if (isCreatingBillingPlan) return;

    createBillingPlan({
      firstBillingDate: data.firstBillingDate.toISOString(),
      terms: [
        {
          termType: "recurring",
          iterations: data.iterations,
          isOpenEnded: false,
          billingDay: data.billingDay,
          candidateAmountCents: data.candidateAmountCents,
          platformFeeCents: data.platformFeeCents,
          totalAmountCents: data.platformFeeCents + data.candidateAmountCents,
        },
      ],
    });
  }

  function prefill() {
    prefillComplete();
    if (!offer) return;

    const firstTerm = offer.terms[0];
    if (!firstTerm) return;
    if (firstTerm.termType !== "recurring") return;

    form.setValue("billingDay", firstTerm.billingDay);
    form.setValue("candidateAmountCents", firstTerm.totalAmountCents);

    if (firstTerm.iterations) {
      form.setValue("iterations", firstTerm.iterations);
    }

    if (typeof firstTerm.platformFeePercentage === "number") {
      const platformFeeCents = Math.round(
        (firstTerm.totalAmountCents * firstTerm.platformFeePercentage) / 100,
      );
      form.setValue("platformFeeCents", platformFeeCents);
    }
  }

  const billingDay = form.watch("billingDay");

  return (
    <>
      <div className="my-3 flex justify-between">
        <Button variant="stroke" className="my-2" onClick={switchMode}>
          <ArrowLeftRight />
          Switch to milestone billing
        </Button>
        {offer?.terms[0]?.termType === "recurring" && (
          <Button variant="lightSecondary" className="mt-2" onClick={prefill}>
            Prefill From Offer
          </Button>
        )}
      </div>
      <Form {...form}>
        {createBillingPlanError && (
          <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
            Something went wrong when making a billing plan, please try again
            later.
          </p>
        )}
        <form
          className="space-y-4 w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="iterations"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Contract duration (months)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    {...form.register("iterations", {
                      valueAsNumber: true,
                    })}
                    type="number"
                    min={1}
                    step={1}
                    placeholder="12"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="candidateAmountCents"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Candidate monthly retainer</FormLabel>
                <FormControl>
                  <CurrencyInput
                    valueCents={field.value}
                    onValueCentsChange={field.onChange}
                    minCents={0}
                    placeholder="10,000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="platformFeeCents"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Venturous monthly fee</FormLabel>
                <FormControl>
                  <CurrencyInput
                    valueCents={field.value}
                    onValueCentsChange={field.onChange}
                    minCents={0}
                    placeholder="10,000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="billingDay"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Billing day of month</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    {...form.register("billingDay", {
                      valueAsNumber: true,
                    })}
                    type="number"
                    min={1}
                    max={31}
                    step={1}
                    placeholder="15"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstBillingDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>First billing date</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    modal
                    validDays={billingDay ? [billingDay] : undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="h-[1px] bg-[#E4E7EC] w-full" />
          <div className="!mb-1 flex justify-between">
            <Button variant="stroke" className="mt-2" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="success"
              type="submit"
              className="mt-2"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={form.handleSubmit(onSubmit)}
            >
              Create Billing Plan
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};

export default CreateBillingPlanForm;
